import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Table, Form, Button, Modal, Alert, Container } from 'react-bootstrap';
import { getTransactionById, processTransaction, deleteTransaction, updateTransactionTimestamp } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const ViewTransaction = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [transaction, setTransaction] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [formData, setFormData] = useState({
        status: '',
        adminNote: ''
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editTimestamp, setEditTimestamp] = useState({
        date: '',
        time: ''
    });

    useEffect(() => {
        if (!id) {
            setError('Transaction ID is required');
            setTimeout(() => navigate('/admin/transactions'), 2000);
            return;
        }
        fetchTransactionDetails();
    }, [id]);

    const fetchTransactionDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getTransactionById(id);
            
            if (response.success) {
                setTransaction(response.data?.transaction || response.data);
                setContext(response.data?.context || null);
            } else {
                throw new Error(response.message || 'Failed to fetch transaction details');
            }
        } catch (error) {
            console.error('Error fetching transaction details:', error);
            setError(error.message || 'Failed to fetch transaction details');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: 'success',
            pending: 'warning',
            failed: 'danger',
            cancelled: 'secondary'
        };
        return badges[status] || 'primary';
    };

    const getTypeColor = (type) => {
        const colors = {
            deposit: 'success',
            withdrawal: 'warning',
            bonus: 'info',
            commission: 'primary'
        };
        return colors[type] || 'secondary';
    };

    const canProcessTransaction = (status) => {
        return ['pending', 'initiated'].includes(status?.toLowerCase());
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProcessTransaction = async (e) => {
        e.preventDefault();
        
        try {
            setProcessing(true);
            
            if (!formData.status) {
                alert('Please select a status');
                return;
            }

            const confirmMessage = `Are you sure you want to mark this transaction as ${formData.status}?`;
            if (!window.confirm(confirmMessage)) {
                return;
            }

            // Prepare API data - send "rejected" for failed or cancelled transactions
            const apiData = {
                ...formData,
                status: (formData.status === 'failed' || formData.status === 'cancelled') ? 'rejected' : formData.status
            };

            const response = await processTransaction(id, apiData);

            if (response.success) {
                alert('Transaction processed successfully');
                await fetchTransactionDetails();
                setFormData({
                    status: '',
                    adminNote: ''
                });
            } else {
                throw new Error(response.message || 'Failed to process transaction');
            }
        } catch (error) {
            console.error('Error processing transaction:', error);
            alert(error.message || 'Failed to process transaction');
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const response = await deleteTransaction(id);
            
            if (response.success) {
                alert('Transaction deleted successfully');
                navigate('/admin/transactions');
            } else {
                throw new Error(response.message || 'Failed to delete transaction');
            }
        } catch (error) {
            console.error('Error deleting transaction:', error);
            alert(error.message || 'Failed to delete transaction');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const handleEditTimestamp = async (e) => {
        e.preventDefault();
        try {
            setProcessing(true);

            // Create the ISO string without timezone conversion
            const timestamp = `${editTimestamp.date}T${editTimestamp.time}Z`;
            const response = await updateTransactionTimestamp(id, timestamp);

            if (response.success) {
                alert(`Transaction timestamp updated successfully`);
                setShowEditModal(false);
                fetchTransactionDetails();
            } else {
                throw new Error(response.message || 'Failed to update timestamp');
            }
        } catch (error) {
            console.error('Error updating timestamp:', error);
            alert(error.message || 'Failed to update timestamp');
        } finally {
            setProcessing(false);
        }
    };

    const openEditModal = () => {
        if (transaction?.createdAt) {
            const currentDate = new Date(transaction.createdAt);
            const year = currentDate.getFullYear();
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const day = String(currentDate.getDate()).padStart(2, '0');
            const hours = String(currentDate.getUTCHours()).padStart(2, '0');
            const minutes = String(currentDate.getUTCMinutes()).padStart(2, '0');
            const seconds = String(currentDate.getUTCSeconds()).padStart(2, '0');
            
            setEditTimestamp({
                date: `${year}-${month}-${day}`,
                time: `${hours}:${minutes}:${seconds}`
            });
            setShowEditModal(true);
        }
    };

    if (loading) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="View Transaction" sidebutton={true} />
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted">Loading transaction details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !transaction) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="View Transaction" sidebutton={true} />
                <Alert variant="danger">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
            </div>
        );
    }

    if (!transaction) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="View Transaction" sidebutton={true} />
                <Alert variant="info">
                    <i className="fa fa-info-circle me-2"></i>
                    No transaction found
                </Alert>
            </div>
        );
    }

    const user = transaction.userId || transaction.user || {};

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="View Transaction" sidebutton={true} />

            {/* Edit Timestamp Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Transaction Timestamp</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditTimestamp}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={editTimestamp.date}
                                        onChange={(e) => setEditTimestamp(prev => ({ ...prev, date: e.target.value }))}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Time (UTC)</Form.Label>
                                    <Form.Control
                                        type="time"
                                        value={editTimestamp.time}
                                        onChange={(e) => setEditTimestamp(prev => ({ ...prev, time: e.target.value }))}
                                        required
                                        step="1"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="text-muted">
                            <small>Note: Time should be in UTC timezone</small>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            type="submit"
                            disabled={processing}
                        >
                            {processing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Updating...
                                </>
                            ) : (
                                'Update Timestamp'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this transaction? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between">
                        <div className="d-flex gap-2">
                            <Button variant="primary" onClick={openEditModal}>
                                <i className="fa fa-edit me-2"></i>Edit Timestamp
                            </Button>
                            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                                <i className="fa fa-trash me-2"></i>Delete
                            </Button>
                        </div>
                        <Link to="/admin/transactions" className="btn btn-outline-secondary">
                            <i className="fa fa-arrow-left me-2"></i>Back to List
                        </Link>
                    </div>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-3">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {error}
                </Alert>
            )}

            <Row>
                <Col xl={8} lg={8}>
                    {/* Transaction Details Card */}
                    <Card className="border-0 shadow-sm mb-3">
                        <Card.Header className="bg-white border-bottom">
                            <Card.Title className="mb-0">Transaction Details</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col lg={6} md={6} sm={12}>
                                    <strong className="d-block text-black mb-1">Reference</strong>
                                    <span>{transaction.reference || '-'}</span>
                                </Col>
                                <Col lg={6} md={6} sm={12}>
                                    <strong className="d-block text-black mb-1">Transaction ID</strong>
                                    <span>{transaction.transactionId || '-'}</span>
                                </Col>
                                <Col lg={6} md={6} sm={12}>
                                    <strong className="d-block text-black mb-1">Type</strong>
                                    <Badge bg={getTypeColor(transaction.type)}>
                                        {transaction.type || '-'}
                                    </Badge>
                                </Col>
                                <Col lg={6} md={6} sm={12}>
                                    <strong className="d-block text-black mb-1">Status</strong>
                                    <Badge bg={getStatusBadge(transaction.status)}>
                                        {transaction.status || '-'}
                                    </Badge>
                                </Col>
                                <Col lg={6} md={6} sm={12}>
                                    <strong className="d-block text-black mb-1">Amount</strong>
                                    <span className="text-primary fw-bold">{formatAmount(transaction.amount)}</span>
                                </Col>
                                <Col lg={6} md={6} sm={12}>
                                    <strong className="d-block text-black mb-1">Medium</strong>
                                    <span>{transaction.medium || '-'}</span>
                                </Col>
                                <Col sm={12}>
                                    <strong className="d-block text-black mb-1">Description</strong>
                                    <span>{transaction.description || '-'}</span>
                                </Col>
                                {transaction.adminNote && (
                                    <Col sm={12}>
                                        <strong className="d-block text-black mb-1">Admin Note</strong>
                                        <span>{transaction.adminNote}</span>
                                    </Col>
                                )}
                                <Col lg={6} md={6} sm={12}>
                                    <strong className="d-block text-black mb-1">Created At</strong>
                                    <span>{formatDate(transaction.createdAt)}</span>
                                </Col>
                                {transaction.processedAt && (
                                    <Col lg={6} md={6} sm={12}>
                                        <strong className="d-block text-black mb-1">Processed At</strong>
                                        <span>{formatDate(transaction.processedAt)}</span>
                                    </Col>
                                )}
                            </Row>

                            {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
                                <>
                                    <hr className="my-4" />
                                    <h5 className="text-black mb-3">Additional Details</h5>
                                    <Row className="g-3">
                                        {transaction.type === 'withdrawal' && transaction.metadata.bankDetails ? (
                                            <>
                                                <Col lg={6} md={6} sm={12}>
                                                    <strong className="d-block text-black mb-1">Account Holder Name</strong>
                                                    <span>{transaction.metadata.bankDetails.accountHolderName || '-'}</span>
                                                </Col>
                                                <Col lg={6} md={6} sm={12}>
                                                    <strong className="d-block text-black mb-1">Account Number</strong>
                                                    <span>{transaction.metadata.bankDetails.accountNumber || '-'}</span>
                                                </Col>
                                                <Col lg={6} md={6} sm={12}>
                                                    <strong className="d-block text-black mb-1">Bank Name</strong>
                                                    <span>{transaction.metadata.bankDetails.bankName || '-'}</span>
                                                </Col>
                                                <Col lg={6} md={6} sm={12}>
                                                    <strong className="d-block text-black mb-1">IFSC Code</strong>
                                                    <span>{transaction.metadata.bankDetails.ifscCode || '-'}</span>
                                                </Col>
                                            </>
                                        ) : (
                                            Object.entries(transaction.metadata).map(([key, value]) => (
                                                <Col lg={6} md={6} sm={12} key={key}>
                                                    <strong className="d-block text-black mb-1">
                                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                                    </strong>
                                                    <span>{value || '-'}</span>
                                                </Col>
                                            ))
                                        )}
                                    </Row>
                                </>
                            )}

                            {transaction.screenshot && (
                                <>
                                    <hr className="my-4" />
                                    <h5 className="text-black mb-3">Screenshot</h5>
                                    <div className="screenshot-container" style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #eee', borderRadius: '4px', padding: '10px' }}>
                                        <img src={transaction.screenshot} alt="Transaction Screenshot" style={{ maxWidth: '100%' }} />
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Process Transaction Card */}
                    {canProcessTransaction(transaction.status) && (
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Process Transaction</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <Form onSubmit={handleProcessTransaction}>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Status</Form.Label>
                                                <Form.Select
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Status</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="failed">Failed</option>
                                                    <option value="cancelled">Cancelled</option>
                                                    <option value="pending">Pending</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Admin Note</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    name="adminNote"
                                                    value={formData.adminNote}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter admin note"
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <div className="mt-3">
                                        <Button
                                            type="submit"
                                            variant={formData.status === 'completed' ? 'success' : 
                                                    formData.status === 'failed' ? 'danger' :
                                                    formData.status === 'cancelled' ? 'secondary' : 'primary'}
                                            disabled={processing}
                                        >
                                            {processing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Processing...
                                                </>
                                            ) : (
                                                'Process Transaction'
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Timeline Card */}
                    {context?.timeline && context.timeline.length > 0 && (
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Transaction Timeline</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="timeline-list" style={{ position: 'relative', padding: '10px 0', maxHeight: '500px', overflowY: 'auto' }}>
                                    <style>{`
                                        .timeline-list::before {
                                            content: '';
                                            position: absolute;
                                            top: 0;
                                            bottom: 0;
                                            left: 20px;
                                            width: 2px;
                                            background: #e9ecef;
                                        }
                                        .timeline-item {
                                            position: relative;
                                            padding-left: 55px;
                                            padding-bottom: 30px;
                                        }
                                        .timeline-badge {
                                            position: absolute;
                                            left: 10px;
                                            width: 22px;
                                            height: 22px;
                                            border-radius: 50%;
                                            text-align: center;
                                            color: #fff;
                                            font-size: 12px;
                                            display: flex;
                                            align-items: center;
                                            justify-content: center;
                                            z-index: 1;
                                        }
                                        .timeline-panel {
                                            padding: 15px;
                                            background: #f8f9fa;
                                            border-radius: 4px;
                                        }
                                        .timeline-title {
                                            color: #2c3e50;
                                            margin: 0;
                                            font-weight: 600;
                                            font-size: 14px;
                                        }
                                    `}</style>
                                    {context.timeline.map((event, index) => {
                                        const isLastItem = index === context.timeline.length - 1;
                                        const getIcon = (action) => {
                                            switch (action) {
                                                case 'created': return 'fa-plus-circle';
                                                case 'processed': return 'fa-check-circle';
                                                case 'completed': return 'fa-check-double';
                                                case 'failed': return 'fa-times-circle';
                                                case 'cancelled': return 'fa-ban';
                                                case 'pending': return 'fa-clock';
                                                default: return 'fa-circle';
                                            }
                                        };
                                        const getIconColor = (action) => {
                                            switch (action) {
                                                case 'created': return '#3b7ddd';
                                                case 'processed': return '#2ecc71';
                                                case 'completed': return '#27ae60';
                                                case 'failed': return '#e74c3c';
                                                case 'cancelled': return '#95a5a6';
                                                case 'pending': return '#f39c12';
                                                default: return '#3498db';
                                            }
                                        };
                                        return (
                                            <div key={index} className={`timeline-item ${isLastItem ? 'last-item' : ''}`}>
                                                <div 
                                                    className="timeline-badge"
                                                    style={{ backgroundColor: getIconColor(event.action) }}
                                                >
                                                    <i className={`fa ${getIcon(event.action)}`}></i>
                                                </div>
                                                <div className="timeline-panel">
                                                    <div className="timeline-heading">
                                                        <h6 className="timeline-title mb-2">{event.description}</h6>
                                                        <div className="d-flex gap-2 flex-wrap">
                                                            <Badge bg="light" text="dark">
                                                                <i className="fa fa-calendar-alt me-1"></i>
                                                                {formatDate(event.timestamp)}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    {event.note && (
                                                        <div className="mt-2">
                                                            <p className="mb-0 text-muted">{event.note}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </Col>

                <Col xl={4} lg={4}>
                    {/* User Details Card */}
                    <Card className="border-0 shadow-sm mb-3">
                        <Card.Header className="bg-white border-bottom">
                            <Card.Title className="mb-0">User Details</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <strong className="d-block text-black mb-1">Name</strong>
                                {user._id ? (
                                    <Link to={`/admin/user/${user._id}`}>{user.name || '-'}</Link>
                                ) : (
                                    <span>{user.name || '-'}</span>
                                )}
                            </div>
                            <div className="mb-3">
                                <strong className="d-block text-black mb-1">Email</strong>
                                <span>{user.email || '-'}</span>
                            </div>
                            <div className="mb-3">
                                <strong className="d-block text-black mb-1">Mobile</strong>
                                <span>{user.mobileNumber || '-'}</span>
                            </div>
                            <div className="mb-3">
                                <strong className="d-block text-black mb-1">Status</strong>
                                <Badge bg={getStatusBadge(user.status)}>
                                    {user.status || '-'}
                                </Badge>
                            </div>
                            {context?.currentWalletBalance !== undefined && (
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">Current Wallet Balance</strong>
                                    <span className="text-primary fw-bold">{formatAmount(context.currentWalletBalance)}</span>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Payment Method Details Card */}
                    {transaction.paymentMethod && (
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Payment Method Details</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">Account Holder</strong>
                                    <span>{transaction.paymentMethod.accountHolderName || '-'}</span>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">Bank Name</strong>
                                    <span>{transaction.paymentMethod.bankName || '-'}</span>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">Account Number</strong>
                                    <span>{transaction.paymentMethod.accountNumber || '-'}</span>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">IFSC Code</strong>
                                    <span>{transaction.paymentMethod.ifscCode || '-'}</span>
                                </div>
                                {transaction.paymentMethod.upiId && (
                                    <div className="mb-3">
                                        <strong className="d-block text-black mb-1">UPI ID</strong>
                                        <span>{transaction.paymentMethod.upiId}</span>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">Status</strong>
                                    <Badge bg={getStatusBadge(transaction.paymentMethod.status)}>
                                        {transaction.paymentMethod.status || '-'}
                                    </Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Related Transactions Card */}
                    {context?.relatedTransactions && context.relatedTransactions.length > 0 && (
                        <Card className="border-0 shadow-sm mb-3">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Related Transactions</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <Table className="table-striped mb-0">
                                        <thead>
                                            <tr>
                                                <th>Reference</th>
                                                <th>Type</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {context.relatedTransactions.map((relatedTx) => (
                                                <tr key={relatedTx._id}>
                                                    <td>
                                                        <Link to={`/admin/transaction/${relatedTx._id}`}>
                                                            {relatedTx.reference}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <Badge bg={getTypeColor(relatedTx.type)}>
                                                            {relatedTx.type}
                                                        </Badge>
                                                    </td>
                                                    <td>{formatAmount(relatedTx.amount)}</td>
                                                    <td>
                                                        <Badge bg={getStatusBadge(relatedTx.status)}>
                                                            {relatedTx.status}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Processed By Card */}
                    {transaction.processedBy && (
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-bottom">
                                <Card.Title className="mb-0">Processed By</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">Name</strong>
                                    <span>{transaction.processedBy.name || '-'}</span>
                                </div>
                                <div className="mb-3">
                                    <strong className="d-block text-black mb-1">Email</strong>
                                    <span>{transaction.processedBy.email || '-'}</span>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default ViewTransaction;

